import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { getLogislyOrderTakerV2HistoryApi } from "../api/logislyOrderTakerV2Api";
import type {
  LogislyOrderTakerConfigV2,
  LogislyOrderTakerV2HistoryResponse,
} from "../types/logislyOrderTakerV2";
import { formatLicensePlateInput } from "../utils/formatLicensePlate";
import { getLogislyOrderTakerV2TruckChipStyle } from "../utils/logislyOrderTakerV2";
import UserAvatar from "../components/common/UserAvatar";
import LogislyOrderTakerV2Route from "../components/logislySpxOnCall/LogislyOrderTakerV2Route";

const dateTime = (value?: string) => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm");
  } catch {
    return value;
  }
};
const statusLabel = (status: LogislyOrderTakerConfigV2["status"]) =>
  status === "FULFILLED"
    ? "Fulfilled"
    : status === "CANCELLED_MANUAL"
      ? "Cancelled manually"
      : "Cancelled: truck taken";
const terminalAt = (config: LogislyOrderTakerConfigV2) =>
  config.status === "FULFILLED" ? config.fulfilledAt : config.cancelledAt;

interface LogislyOrderTakerConfigsV2HistorySectionProps {
  embedded?: boolean;
}

export const LogislyOrderTakerConfigsV2HistorySection = ({
  embedded = false,
}: LogislyOrderTakerConfigsV2HistorySectionProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<LogislyOrderTakerV2HistoryResponse>({
    configs: [],
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPreviousPage: false,
    },
  });
  const [loading, setLoading] = useState(true);
  const query = useMemo(
    () => ({
      page: Number(searchParams.get("page") || 1),
      limit: 20,
      status: searchParams.get("status") || undefined,
      q: searchParams.get("q") || undefined,
      from: searchParams.get("from") || undefined,
      to: searchParams.get("to") || undefined,
    }),
    [searchParams],
  );
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getLogislyOrderTakerV2HistoryApi(query));
    } finally {
      setLoading(false);
    }
  }, [query]);
  useEffect(() => {
    load().catch((error) => console.error("Failed to load Logisly V2 history", error));
  }, [load]);
  const updateQuery = (next: Record<string, string | undefined>) => {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(next)) {
      if (value) params.set(key, value);
      else params.delete(key);
    }
    if (!("page" in next)) params.set("page", "1");
    setSearchParams(params);
  };
  return (
    <Box>
      {embedded ? (
        <Typography variant="h5" component="h2" sx={{ mb: 0.5 }}>
          Completed / Cancelled Configs
        </Typography>
      ) : (
        <>
          <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
            Order Taker Configs V2 History
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Completed and cancelled configs, including the route of any fulfilled order.
          </Typography>
        </>
      )}
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={searchParams.get("q") || ""}
          onChange={(event) => updateQuery({ q: event.target.value || undefined })}
          placeholder="ID, order, truck, driver, route"
          fullWidth
        />
        <TextField
          select
          size="small"
          label="Status"
          value={searchParams.get("status") || ""}
          onChange={(event) => updateQuery({ status: event.target.value || undefined })}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="FULFILLED">Fulfilled</MenuItem>
          <MenuItem value="CANCELLED_MANUAL">Cancelled</MenuItem>
          <MenuItem value="CANCELLED_TRUCK_TAKEN">Truck taken</MenuItem>
        </TextField>
        <TextField
          size="small"
          type="date"
          label="From"
          value={searchParams.get("from") || ""}
          onChange={(event) => updateQuery({ from: event.target.value || undefined })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={searchParams.get("to") || ""}
          onChange={(event) => updateQuery({ to: event.target.value || undefined })}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
      {loading ? (
        <Typography color="text.secondary">Loading history…</Typography>
      ) : data.configs.length === 0 ? (
        <Typography color="text.secondary">
          No completed or cancelled configs match these filters.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Route</TableCell>
              <TableCell>Truck</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Creator</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Fulfilled order</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.configs.map((config) => (
              <TableRow key={config._id}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{config.orderTakerConfigId}</TableCell>
                <TableCell>
                  <LogislyOrderTakerV2Route config={config} />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Chip
                    size="small"
                    label={
                      config.truck?.logisly?.number
                        ? formatLicensePlateInput(config.truck.logisly.number)
                        : config.logislyTruckId
                    }
                    sx={getLogislyOrderTakerV2TruckChipStyle(
                      config.truck?.logisly?.number ?? config.logislyTruckId,
                    )}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {config.driver?.logisly?.name || config.logislyDriverId}
                  </Typography>
                  {config.driver?.logisly?.phone && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {config.driver.logisly.phone}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <UserAvatar
                    user={
                      config.createdBy || {
                        _id: config.createdByUserId,
                        name: config.createdByUserId,
                      }
                    }
                    tooltipText={
                      config.createdBy?.username || config.createdBy?.name || config.createdByUserId
                    }
                    size={28}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={statusLabel(config.status)}
                    color={config.status === "FULFILLED" ? "success" : "warning"}
                  />
                  <Typography variant="caption" display="block">
                    {dateTime(terminalAt(config))}
                  </Typography>
                  {config.status !== "CANCELLED_MANUAL" && config.cancelReason && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {config.cancelReason}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {config.status === "FULFILLED" ? (
                    <>
                      <Typography variant="body2">
                        {config.fulfilledOrderNumber || config.fulfilledOrderId || "-"}
                      </Typography>
                      <Typography variant="caption">
                        {[...(config.fulfilledOrderRoute ?? [])]
                          .sort((a, b) => a.sequence - b.sequence)
                          .map((node) => node.name)
                          .join(" → ") || "No order route reported"}
                      </Typography>
                    </>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mt: 3 }}>
        <Pagination
          count={Math.max(1, data.pagination.totalPages)}
          page={data.pagination.page}
          onChange={(_, page) => updateQuery({ page: String(page) })}
          color="primary"
          showFirstButton
          showLastButton
        />
        <Typography variant="caption" color="text.secondary">
          {data.pagination.total} result{data.pagination.total === 1 ? "" : "s"}
        </Typography>
      </Box>
    </Box>
  );
};

const LogislyOrderTakerConfigsV2HistoryPage = () => <LogislyOrderTakerConfigsV2HistorySection />;

export default LogislyOrderTakerConfigsV2HistoryPage;
